import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Info, ExternalLink, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import EnhancedLoginAlert from './EnhancedLoginAlert';

const LoginPage = () => {
  const { user, loading, signInWithGoogle } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  
  // Check if we're in preview iframe environment
  const isInPreviewIframe = window.self !== window.top;
  
  // Check if user was just denied access
  const isDenied = searchParams.get('denied') === '1';

  const handleRetryAuth = async () => {
    setAuthError(null);
    setIsAuthenticating(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setIsAuthenticating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Đang tải...</p>
        </div>
      </div>
    );
  }

  // Don't auto-redirect if user was just denied (race condition prevention)
  if (user && !isDenied) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Đăng nhập</CardTitle>
          <CardDescription>
            Đăng nhập vào hệ thống quản lý dự án
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <EnhancedLoginAlert />
          {isInPreviewIframe && (
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
              <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                <div className="space-y-2">
                  <p className="font-medium">Môi trường Preview</p>
                  <p className="text-sm">
                    Đăng nhập sẽ mở popup Google OAuth. Popup sẽ tự đóng sau khi hoàn tất.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          {authError && (
            <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
              <AlertDescription className="text-red-800 dark:text-red-200">
                <div className="space-y-2">
                  <p className="font-medium">Lỗi đăng nhập</p>
                  <p className="text-sm">{authError}</p>
                  <Button 
                    onClick={handleRetryAuth}
                    variant="outline"
                    size="sm"
                    className="mt-2"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Thử lại
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Button 
            onClick={async () => {
              setAuthError(null);
              setIsAuthenticating(true);
              try {
                await signInWithGoogle();
              } catch (error) {
                setAuthError(error instanceof Error ? error.message : 'Authentication failed');
              } finally {
                setIsAuthenticating(false);
              }
            }}
            className="w-full"
            variant="outline"
            disabled={isAuthenticating}
          >
            {isAuthenticating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
            ) : (
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            {isAuthenticating ? 'Đang đăng nhập...' : 'Đăng nhập bằng Google'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;